import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Maximize2, Minimize2 } from 'lucide-react';
import type {
  PlaygroundLLMCall,
  PlaygroundMemoryEntity,
  PlaygroundRun,
  PlaygroundSkillDiag,
  PlaygroundSkillStepDiag,
  PlaygroundStepDiag,
} from '@/entities/playground';
import { getPlaygroundMemory } from '@/entities/playground';
import { KnowledgeEntityDetails } from '@/entities/knowledge';
import { ApiError } from '@/shared/api';
import { labelOf, useI18n, type Messages } from '@/shared/i18n';
import { formatDurationMs } from '@/shared/lib/format-duration';
import { cn } from '@/shared/lib/utils';
import { KnowledgeFlowCanvas, type KnowledgeFlowSelection } from '@/shared/ui/knowledge-flow';
import { RunFlow, type RunFlowNode } from '@/shared/ui/run-flow';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { ScrollArea } from '@/shared/ui/shadcn/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

function JsonBlock({ value, label }: { value: unknown; label: string }) {
  if (value === undefined || value === null) {
    return null;
  }
  return (
    <details className="group rounded-md border border-border/60 bg-muted/30">
      <summary className="cursor-pointer px-2 py-1.5 text-xs font-medium text-muted-foreground">
        {label}
      </summary>
      <ScrollArea className="max-h-48 border-t border-border/60">
        <pre className="px-2 py-1.5 text-[11px] leading-relaxed break-words whitespace-pre-wrap">
          {JSON.stringify(value, null, 2)}
        </pre>
      </ScrollArea>
    </details>
  );
}

function TextBlock({
  value,
  label,
  open = true,
}: {
  value: unknown;
  label: string;
  open?: boolean;
}) {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  return (
    <details className="rounded-md border border-border/60 bg-muted/30" open={open}>
      <summary className="cursor-pointer px-2 py-1.5 text-xs font-medium text-muted-foreground">
        {label}
      </summary>
      <ScrollArea className="max-h-56 border-t border-border/60">
        <pre className="px-2 py-1.5 text-[11px] leading-relaxed break-words whitespace-pre-wrap">
          {value}
        </pre>
      </ScrollArea>
    </details>
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

/** Prefer readable guidance text; omit it from the JSON dump when shown separately. */
function splitGuidance(returned: unknown): {
  guidance: string | null;
  rest: Record<string, unknown> | null;
} {
  const record = asRecord(returned);
  if (!record) return { guidance: null, rest: asRecord(returned) };
  const guidance =
    typeof record.generation_instructions === 'string' ? record.generation_instructions : null;
  if (!guidance) return { guidance: null, rest: record };
  const rest = { ...record };
  delete rest.generation_instructions;
  return { guidance, rest: Object.keys(rest).length > 0 ? rest : null };
}

type SelectedNode =
  | { kind: 'step'; step: PlaygroundStepDiag }
  | { kind: 'skill'; skill: PlaygroundSkillDiag; parent: PlaygroundStepDiag }
  | { kind: 'skill_step'; step: PlaygroundSkillStepDiag; skill: PlaygroundSkillDiag }
  | { kind: 'llm'; call: PlaygroundLLMCall; parent: PlaygroundStepDiag }
  | { kind: 'context'; context: Record<string, unknown> };

function buildNodes(
  run: PlaygroundRun,
  copy: Messages,
): {
  nodes: Omit<RunFlowNode, 'expandedContent'>[];
  byId: Map<string, SelectedNode>;
} {
  const nodes: Omit<RunFlowNode, 'expandedContent'>[] = [];
  const byId = new Map<string, SelectedNode>();

  run.diagnostics.steps.forEach((step, stepIndex) => {
    const stepId = `step-${stepIndex}-${step.step_key}`;
    byId.set(stepId, { kind: 'step', step });
    const returned = asRecord(step.returned);
    let subtitle = step.action;
    if (step.status === 'skipped' && typeof returned?.when_expr === 'string') {
      subtitle = copy.diagnostics.skipped(returned.when_expr);
    } else if (step.status === 'not_reached') {
      subtitle = copy.diagnostics.notReached;
    } else if (
      (step.action === 'run_skill' || step.action === 'skill_exec') &&
      typeof returned?.note === 'string'
    ) {
      subtitle = `${step.action} · ${returned.note}`;
    }
    nodes.push({
      id: stepId,
      kind: 'step',
      title: step.step_key.replaceAll('_', ' '),
      subtitle,
      status: step.status,
      durationMs:
        step.duration_ms ?? (step.status === 'skipped' || step.status === 'not_reached' ? 0 : null),
      meta:
        step.tokens_in != null || step.tokens_out != null
          ? copy.diagnostics.tokens(String(step.tokens_in ?? '—'), String(step.tokens_out ?? '—'))
          : undefined,
      depth: 0,
    });

    (step.skills ?? []).forEach((skill, skillIndex) => {
      const skillId = `${stepId}-skill-${skillIndex}`;
      byId.set(skillId, { kind: 'skill', skill, parent: step });
      nodes.push({
        id: skillId,
        kind: 'skill',
        title: skill.skill_key || 'skill',
        subtitle:
          skill.query_key != null
            ? copy.diagnostics.query(String(skill.query_key))
            : skill.tool_source,
        status: skill.status,
        durationMs: skill.duration_ms ?? null,
        depth: 1,
      });

      (skill.steps ?? []).forEach((skillStep, skillStepIndex) => {
        const skillStepId = `${skillId}-step-${skillStepIndex}`;
        byId.set(skillStepId, { kind: 'skill_step', step: skillStep, skill });
        nodes.push({
          id: skillStepId,
          kind: 'skill_step',
          title: skillStep.step_key.replaceAll('_', ' ') || skillStep.tool,
          subtitle: skillStep.tool,
          status: skillStep.status,
          durationMs: skillStep.duration_ms ?? null,
          depth: 2,
        });
      });
    });

    (step.llm_calls ?? []).forEach((call, callIndex) => {
      const llmId = `${stepId}-llm-${callIndex}`;
      byId.set(llmId, { kind: 'llm', call, parent: step });
      nodes.push({
        id: llmId,
        kind: 'llm',
        title: labelOf(copy.diagnostics.llm, call.purpose),
        subtitle: call.model,
        status: call.error ? 'failed' : 'succeeded',
        durationMs: call.duration_ms ?? null,
        meta:
          call.tokens_in != null || call.tokens_out != null
            ? copy.diagnostics.tokens(String(call.tokens_in ?? '—'), String(call.tokens_out ?? '—'))
            : undefined,
        depth: 1,
      });
    });
  });

  if (run.context && Object.keys(run.context).length > 0) {
    const contextId = 'final-context';
    byId.set(contextId, { kind: 'context', context: run.context });
    nodes.push({
      id: contextId,
      kind: 'context',
      title: copy.diagnostics.contextAfter,
      subtitle: copy.diagnostics.contextHint,
      depth: 0,
    });
  }

  return { nodes, byId };
}

function TimingLine({ ms, label }: { ms: number | null | undefined; label?: string }) {
  const { t } = useI18n();
  const caption = label ?? t.common.duration;
  return (
    <p className="text-xs text-muted-foreground">
      {caption}: <span className="font-mono tabular-nums">{formatDurationMs(ms)}</span>
      {ms != null && ms >= 0 ? (
        <span className="text-muted-foreground/70"> ({Math.round(ms)}ms)</span>
      ) : null}
    </p>
  );
}

function NodeDetail({ selected }: { selected: SelectedNode }) {
  const { t } = useI18n();
  if (selected.kind === 'step') {
    const { step } = selected;
    const returned = asRecord(step.returned);
    const whenExpr = typeof returned?.when_expr === 'string' ? returned.when_expr : null;
    const onFalse = typeof returned?.on_false === 'string' ? returned.on_false : null;
    const resolved = returned?.resolved;
    const notReachedReason = typeof returned?.reason === 'string' ? returned.reason : null;
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">{t.diagnostics.tool(step.action)}</p>
        <TimingLine
          ms={
            step.duration_ms ??
            (step.status === 'skipped' || step.status === 'not_reached' ? 0 : null)
          }
        />
        {(step.tokens_in != null || step.tokens_out != null || step.model) && (
          <p className="text-[11px] text-muted-foreground">
            {step.model ? `${step.model} · ` : ''}
            tokens {step.tokens_in ?? '—'} / {step.tokens_out ?? '—'}
          </p>
        )}
        {step.error ? <p className="text-xs text-destructive">{step.error}</p> : null}
        {step.status === 'skipped' && whenExpr ? (
          <div className="rounded-md border border-border/60 bg-muted/30 px-2 py-1.5 text-xs">
            <p className="font-medium text-muted-foreground">{t.diagnostics.skippedCondition}</p>
            <p className="mt-1 font-mono text-[11px]">{whenExpr}</p>
            {onFalse ? (
              <p className="mt-1 text-muted-foreground">
                {onFalse === 'end' ? t.diagnostics.ifFalseStop : t.diagnostics.ifFalseContinue}
              </p>
            ) : null}
            <JsonBlock value={resolved} label={t.diagnostics.resolved} />
          </div>
        ) : null}
        {step.status === 'not_reached' && notReachedReason ? (
          <p className="text-xs text-muted-foreground">
            {t.diagnostics.notReachedReason(notReachedReason.replaceAll('_', ' '))}
          </p>
        ) : null}
        <JsonBlock value={step.received} label={t.diagnostics.contextIn} />
        <JsonBlock value={step.returned} label={t.diagnostics.contextOut} />
      </div>
    );
  }

  if (selected.kind === 'skill') {
    const { skill } = selected;
    const { guidance, rest } = splitGuidance(skill.returned);
    return (
      <div className="space-y-2">
        <TimingLine ms={skill.duration_ms} />
        {skill.skill_version_id ? (
          <p className="font-mono text-[11px] text-muted-foreground">
            {t.diagnostics.version(skill.skill_version_id.slice(0, 8))}
          </p>
        ) : null}
        {skill.query_key != null ? (
          <p className="text-xs text-muted-foreground">
            {t.diagnostics.query(String(skill.query_key))}
          </p>
        ) : null}
        {skill.status === 'empty' ? (
          <p className="text-xs text-amber-700 dark:text-amber-400">{t.diagnostics.emptySkill}</p>
        ) : null}
        {skill.error ? <p className="text-xs text-destructive">{skill.error}</p> : null}
        <JsonBlock value={skill.received} label={t.diagnostics.received} />
        <TextBlock value={guidance} label={t.diagnostics.guidance} />
        <JsonBlock value={rest} label={t.diagnostics.returned} />
      </div>
    );
  }

  if (selected.kind === 'skill_step') {
    const { step } = selected;
    const { guidance, rest } = splitGuidance(step.returned);
    return (
      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">{t.diagnostics.tool(step.tool)}</p>
        <TimingLine ms={step.duration_ms} />
        {step.status === 'empty' ? (
          <p className="text-xs text-amber-700 dark:text-amber-400">{t.diagnostics.emptyStep}</p>
        ) : null}
        {step.error ? <p className="text-xs text-destructive">{step.error}</p> : null}
        <JsonBlock value={step.received} label={t.diagnostics.received} />
        <TextBlock
          value={guidance}
          label={step.tool === 'emit_guidance' ? t.diagnostics.guidance : t.diagnostics.generation}
        />
        <JsonBlock
          value={rest ?? (guidance ? null : step.returned)}
          label={t.diagnostics.returned}
        />
      </div>
    );
  }

  if (selected.kind === 'llm') {
    const { call } = selected;
    return (
      <div className="space-y-2">
        <TimingLine ms={call.duration_ms} />
        {(call.tokens_in != null || call.tokens_out != null) && (
          <p className="text-[11px] text-muted-foreground">
            {t.diagnostics.tokens(String(call.tokens_in ?? '—'), String(call.tokens_out ?? '—'))}
          </p>
        )}
        {call.error ? <p className="text-xs text-destructive">{call.error}</p> : null}
        <JsonBlock value={call.request_messages} label={t.diagnostics.requestMessages} />
        {call.response_content ? (
          <details className="rounded-md border border-border/60 bg-muted/30" open>
            <summary className="cursor-pointer px-2 py-1.5 text-xs font-medium text-muted-foreground">
              {t.diagnostics.response}
            </summary>
            <ScrollArea className="max-h-48 border-t border-border/60">
              <pre className="px-2 py-1.5 text-[11px] leading-relaxed break-words whitespace-pre-wrap">
                {call.response_content}
              </pre>
            </ScrollArea>
          </details>
        ) : null}
      </div>
    );
  }

  return <JsonBlock value={selected.context} label={t.diagnostics.finalContext} />;
}

export type PlaygroundDiagnosticsTab = 'thinking' | 'memory' | 'remarks';

export interface PlaygroundDiagnosticsPanelProps {
  dialogId: string | null;
  /** Bumps memory refetch after each completed run. */
  memoryRevision?: string | null;
  run: PlaygroundRun | undefined;
  isLoading: boolean;
  errorMessage?: string | null;
  /** Rendered inside the Remarks tab; composed by the page (widgets stay independent). */
  remarksContent?: ReactNode;
  tab?: PlaygroundDiagnosticsTab;
  onTabChange?: (tab: PlaygroundDiagnosticsTab) => void;
  className?: string;
}

function MemoryPanel({
  dialogId,
  memoryRevision,
}: {
  dialogId: string | null;
  memoryRevision?: string | null;
}) {
  const { t } = useI18n();
  const [selected, setSelected] = useState<KnowledgeFlowSelection | null>(null);
  const [graphExpanded, setGraphExpanded] = useState(false);
  const memoryQuery = useQuery({
    queryKey: ['playground-memory', dialogId, memoryRevision],
    queryFn: () => getPlaygroundMemory(dialogId!),
    enabled: Boolean(dialogId),
  });

  useEffect(() => {
    setSelected(null);
    setGraphExpanded(false);
  }, [dialogId, memoryRevision]);

  useEffect(() => {
    if (!graphExpanded || selected) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setGraphExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [graphExpanded, selected]);

  if (!dialogId) {
    return (
      <p className="px-3 py-6 text-center text-xs text-muted-foreground">
        {t.diagnostics.startMemory}
      </p>
    );
  }

  if (memoryQuery.isLoading || memoryQuery.isFetching) {
    return (
      <div className="flex items-center gap-2 px-3 py-8 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t.diagnostics.loadingMemory}
      </div>
    );
  }

  if (memoryQuery.error) {
    return (
      <p className="px-3 text-sm text-destructive">
        {memoryQuery.error instanceof ApiError
          ? memoryQuery.error.message
          : t.diagnostics.memoryFailed}
      </p>
    );
  }

  const memory = memoryQuery.data;
  if (!memory) return null;

  const hasSummaries =
    Boolean(memory.interlocutor_summary.trim()) || Boolean(memory.dialog_summary.trim());
  const hasGraph = memory.entities.length > 0;
  const empty = !hasSummaries && memory.items.length === 0 && !hasGraph;

  const selectedEntity: PlaygroundMemoryEntity | null = selected
    ? (memory.entities.find(
        (entity) => entity.label === selected.label && entity.id === selected.id,
      ) ?? null)
    : null;

  const graphCanvas = (
    <KnowledgeFlowCanvas
      key={graphExpanded ? 'expanded' : 'panel'}
      entities={memory.entities}
      edges={memory.edges}
      selected={selected}
      onSelect={setSelected}
    />
  );

  const expandToggle = (
    <Button
      type="button"
      size="icon-sm"
      variant="outline"
      className="absolute top-2 right-2 z-10 bg-background/90 shadow-sm backdrop-blur-sm"
      aria-label={graphExpanded ? t.diagnostics.collapseGraph : t.diagnostics.expandGraph}
      onClick={() => setGraphExpanded((value) => !value)}
    >
      {graphExpanded ? <Minimize2 /> : <Maximize2 />}
    </Button>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {empty ? (
        <p className="px-3 py-6 text-center text-xs text-muted-foreground">
          {t.diagnostics.noMemory}
        </p>
      ) : null}

      {(hasSummaries || memory.items.length > 0) && (
        <ScrollArea className="max-h-[30%] shrink-0 border-b border-border" viewportClassName="space-y-3 p-3">
          {memory.interlocutor_summary.trim() ? (
            <section className="space-y-1.5">
              <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                {t.diagnostics.interlocutorSummary}
              </p>
              <p className="rounded-md border border-border/60 bg-background px-2.5 py-2 text-xs leading-relaxed whitespace-pre-wrap">
                {memory.interlocutor_summary}
              </p>
            </section>
          ) : null}

          {memory.dialog_summary.trim() ? (
            <section className="space-y-1.5">
              <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                {t.diagnostics.dialogSummary}
              </p>
              <p className="rounded-md border border-border/60 bg-background px-2.5 py-2 text-xs leading-relaxed whitespace-pre-wrap">
                {memory.dialog_summary}
              </p>
            </section>
          ) : null}

          {memory.items.length > 0 ? (
            <section className="space-y-2">
              <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                {t.diagnostics.slots(memory.items.length)}
              </p>
              <ul className="space-y-2">
                {memory.items.map((item) => (
                  <li
                    key={item.id}
                    className="rounded-md border border-border/60 bg-background px-2.5 py-2"
                  >
                    <div className="mb-1 flex flex-wrap items-center gap-1.5">
                      {item.key ? (
                        <span className="font-mono text-xs font-medium">{item.key}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {t.diagnostics.untitled}
                        </span>
                      )}
                      <Badge variant="outline" className="font-normal capitalize">
                        {item.scope}
                      </Badge>
                      <Badge variant="outline" className="font-normal capitalize">
                        {item.origin.replaceAll('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-xs leading-relaxed whitespace-pre-wrap">{item.content}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </ScrollArea>
      )}

      {hasGraph ? (
        <div className="relative min-h-0 flex-1 overflow-hidden">
          {!graphExpanded ? (
            <>
              {expandToggle}
              {graphCanvas}
            </>
          ) : null}
          {graphExpanded
            ? createPortal(
                <div className="fixed inset-0 z-40 bg-background">
                  {expandToggle}
                  {graphCanvas}
                </div>,
                document.body,
              )
            : null}
        </div>
      ) : null}

      <Dialog
        open={Boolean(selectedEntity)}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent
          className="max-h-[min(80vh,640px)] gap-0 overflow-hidden p-0 sm:max-w-lg"
          showCloseButton
        >
          <DialogHeader className="sr-only">
            <DialogTitle>
              {selectedEntity
                ? `${selectedEntity.label} ${selectedEntity.id}`
                : t.knowledge.entityDetails}
            </DialogTitle>
            <DialogDescription>{t.knowledge.entityHint}</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[min(80vh,640px)]">
            <KnowledgeEntityDetails
              entity={selectedEntity}
              edges={memory.edges}
              emptyLabel={t.knowledge.selectNodeShort}
            />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RunSummary({ run }: { run: PlaygroundRun }) {
  const { t } = useI18n();
  const diag = run.diagnostics;
  const hasMeta =
    diag.model ||
    diag.duration_ms != null ||
    run.tokens_in != null ||
    (diag.skills_loaded?.length ?? 0) > 0 ||
    diag.system_prompt_hash;
  if (!hasMeta) return null;
  return (
    <div className="space-y-1.5 rounded-md border border-border/60 bg-muted/30 px-2.5 py-2 text-[11px] text-muted-foreground">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {diag.model ? <span className="font-mono">{diag.model}</span> : null}
        {diag.duration_ms != null ? <span>{formatDurationMs(diag.duration_ms)}</span> : null}
        {run.tokens_in != null || run.tokens_out != null ? (
          <span>
            {t.diagnostics.tokens(String(run.tokens_in ?? '—'), String(run.tokens_out ?? '—'))}
          </span>
        ) : null}
        {diag.system_prompt_hash ? (
          <span
            className="font-mono"
            title={t.diagnostics.promptChars(String(diag.system_prompt_chars ?? '?'))}
          >
            {t.diagnostics.prompt} {diag.system_prompt_hash}
          </span>
        ) : null}
      </div>
      {(diag.skills_loaded?.length ?? 0) > 0 || (diag.allowed_skills?.length ?? 0) > 0 ? (
        <div className="flex flex-wrap items-center gap-1">
          <span>{t.diagnostics.skills}</span>
          {(diag.allowed_skills ?? []).map((skill) => {
            const loaded = diag.skills_loaded?.includes(skill);
            return (
              <Badge
                key={skill}
                variant={loaded ? 'secondary' : 'outline'}
                className={cn('font-mono font-normal', !loaded && 'opacity-60')}
              >
                {skill}
              </Badge>
            );
          })}
          {(diag.skills_loaded ?? [])
            .filter((skill) => !(diag.allowed_skills ?? []).includes(skill))
            .map((skill) => (
              <Badge key={skill} variant="secondary" className="font-mono font-normal">
                {skill}
              </Badge>
            ))}
        </div>
      ) : null}
    </div>
  );
}

function ThinkingPanel({
  run,
  isLoading,
  errorMessage,
}: {
  run: PlaygroundRun | undefined;
  isLoading: boolean;
  errorMessage?: string | null;
}) {
  const { t } = useI18n();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { nodes, byId } = useMemo(() => {
    if (!run)
      return {
        nodes: [] as Omit<RunFlowNode, 'expandedContent'>[],
        byId: new Map<string, SelectedNode>(),
      };
    return buildNodes(run, t);
  }, [run, t]);

  useEffect(() => {
    setExpandedId(null);
  }, [run?.id]);

  const nodesWithExpansion: RunFlowNode[] = nodes.map((node) => {
    const payload = byId.get(node.id);
    return {
      ...node,
      selected: node.id === expandedId,
      expandedContent: payload ? <NodeDetail selected={payload} /> : undefined,
    };
  });

  const handleSelect = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t.diagnostics.loadingRun}
        </div>
      ) : null}
      {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
      {!isLoading && !errorMessage && run ? <RunSummary run={run} /> : null}
      {!isLoading && !errorMessage && run?.error ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1.5 text-xs text-destructive">
          {run.error}
        </p>
      ) : null}
      {!isLoading && !errorMessage && run ? (
        <RunFlow nodes={nodesWithExpansion} onSelect={handleSelect} empty={t.diagnostics.noSteps} />
      ) : null}
      {!isLoading && !errorMessage && !run ? (
        <p className="py-6 text-center text-xs text-muted-foreground">{t.diagnostics.stepsHint}</p>
      ) : null}
    </div>
  );
}

export function PlaygroundDiagnosticsPanel({
  dialogId,
  memoryRevision,
  run,
  isLoading,
  errorMessage,
  remarksContent,
  tab,
  onTabChange,
  className,
}: PlaygroundDiagnosticsPanelProps) {
  const { t } = useI18n();
  const [internalTab, setInternalTab] = useState<PlaygroundDiagnosticsTab>('thinking');
  const activeTab = tab ?? internalTab;
  const changeTab = (value: string) => {
    const next = value as PlaygroundDiagnosticsTab;
    setInternalTab(next);
    onTabChange?.(next);
  };
  return (
    <aside
      className={cn(
        'flex h-full min-h-0 w-full min-w-0 shrink flex-col border-l border-border bg-muted/20 desktop:w-[420px] desktop:max-w-[420px]',
        className,
      )}
    >
      <Tabs
        value={activeTab}
        onValueChange={changeTab}
        className="flex h-full min-h-0 flex-col gap-0"
      >
        <div className="shrink-0 border-b border-border px-3 pt-3">
          <TabsList variant="line" className="w-full">
            <TabsTrigger value="thinking" className="flex-1 after:bottom-0">
              {t.diagnostics.thinking}
            </TabsTrigger>
            <TabsTrigger value="memory" className="flex-1 after:bottom-0">
              {t.diagnostics.memory}
            </TabsTrigger>
            {remarksContent !== undefined ? (
              <TabsTrigger value="remarks" className="flex-1 after:bottom-0">
                {t.diagnostics.remarks}
              </TabsTrigger>
            ) : null}
          </TabsList>
        </div>

        <TabsContent
          value="thinking"
          className="min-h-0 flex-1 overflow-hidden data-[hidden]:hidden"
        >
          <ScrollArea className="h-full min-h-0">
            <div className="p-3">
              <ThinkingPanel run={run} isLoading={isLoading} errorMessage={errorMessage} />
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="memory" className="min-h-0 flex-1 overflow-hidden data-[hidden]:hidden">
          <div className="h-full min-h-0">
            <MemoryPanel dialogId={dialogId} memoryRevision={memoryRevision} />
          </div>
        </TabsContent>

        {remarksContent !== undefined ? (
          <TabsContent
            value="remarks"
            className="min-h-0 flex-1 overflow-hidden data-[hidden]:hidden"
          >
            <div className="h-full min-h-0">{remarksContent}</div>
          </TabsContent>
        ) : null}
      </Tabs>
    </aside>
  );
}
